/**
 * `Shell/Toolbar` (node 6:446) — the stage tabs, the filters and the view toggle.
 *
 * Measured off the node:
 *  · `StageTabs` (6:447): `bg/subtle` track, `radius/sm`, p-3, 2px gaps; each tab h-30,
 *    px-10, `radius` 6. ACTIVE = `bg/surface` + `0 1px 2px rgba(26,31,51,.1)`, label
 *    `Body/13` in `text/primary`, count `Caption/12` in `accent/indigo`. Inactive = label
 *    `text/secondary`, count `text/muted`.
 *  · `ViewToggle` (6:473): two 32px cells inside one `border/default` `radius/sm` box.
 *  · Filter chips (6:467-471): h-32, px-10, `bg/surface`, `border/default`, `radius/sm`,
 *    `Body/13` `text/secondary`.
 *
 * ===========================================================================
 * THREE OF THE FIVE TOOLBAR CONTROLS ARE NOT BUILT, AND ONE IS ADDED
 * ===========================================================================
 * NOT BUILT, because the endpoint they would drive does not exist:
 *  · **Date ▾** — `GET /countries/:cc/inquiries` takes `stage`, `assigned`, `q`, `page`,
 *    `perPage` and nothing else (`InquiryListQuery`). There is no date filter to send, so
 *    the chip would open a picker that changed nothing.
 *  · **More filters** — with `stage`, `assigned` and `q` already on screen, the sheet
 *    behind this chip would be empty.
 *  · **Export** — there is no export route in Phase 2, and `reports.export` is a Phase 5
 *    permission. A button that downloads nothing is worse than no button.
 * Rendering any of the three dead would teach an operator that the toolbar lies.
 *
 * ADDED, as a recorded extension: a **search field**. `?q=` is real, the brief lists it,
 * and the design's only search is the global TopBar box — which the foundation shipped as
 * an explicitly disabled affordance whose own tooltip says "until then, use the search on
 * each list" (`components/shell/TopBar.tsx`). This is that search. It is drawn in the
 * chip's exact geometry so it reads as one of the row rather than as a new idea.
 *
 * **Owner ▾** IS built, but with the three options the API can actually answer:
 * `?assigned=<userId>`, `?assigned=none`, and absent. There is deliberately no user
 * picker: listing users needs `users.manage`, which no Tech Support user holds, so a
 * "pick a colleague" menu would 403 for the very people this phase ships for.
 */

import { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, Columns3, Rows3, Search, X } from 'lucide-react';
import { BOARD_STAGES, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import { cn } from '../../lib/cn';
import { UNASSIGNED } from './query-state';
import type { InquiryQueryState, InquiryView } from './query-state';

/* ────────────────────────────────────────────────────────────── stage tabs ── */

const TAB_BASE =
  'flex h-[30px] shrink-0 items-center justify-center gap-1.5 rounded-[6px] px-2.5 ' +
  'text-body13 transition-colors';
const TAB_ACTIVE = 'bg-surface text-fg shadow-tab-active';
const TAB_IDLE = 'text-fg-secondary hover:text-fg';

export function StageTabs({
  stage,
  counts,
  total,
  onSelect,
}: {
  stage: InquiryStage | null;
  /** `null` while stage-counts is loading or refused — the tabs then show no numbers. */
  counts: Readonly<Record<InquiryStage, number>> | null;
  total: number | null;
  onSelect: (stage: InquiryStage | null) => void;
}): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Filter by stage"
      className="flex flex-wrap items-center gap-0.5 rounded-sm bg-subtle p-[3px]"
    >
      <Tab
        label="All"
        count={total}
        active={stage === null}
        onSelect={() => onSelect(null)}
      />
      {BOARD_STAGES.map((candidate) => (
        <Tab
          key={candidate}
          label={stageTabLabel(candidate)}
          count={counts?.[candidate] ?? null}
          active={stage === candidate}
          onSelect={() => onSelect(candidate)}
        />
      ))}
    </div>
  );
}

function Tab({
  label,
  count,
  active,
  onSelect,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onSelect}
      className={cn(TAB_BASE, active ? TAB_ACTIVE : TAB_IDLE)}
    >
      {label}
      {count === null ? null : (
        <span className={cn('tnum text-label', active ? 'text-indigo' : 'text-fg-muted')}>
          {count}
        </span>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── chips ── */

const CHIP =
  'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 ' +
  'text-body13 text-fg-secondary hover:text-fg';

/**
 * Owner filter.
 *
 * `me` is resolved to the signed-in user's id by the caller, because `?assigned=` takes a
 * user id and the API deliberately has no `me` alias ("the client holds its own `sub` and
 * can send it, and a server-side alias would be a second way to express one filter" —
 * `inquiry-filters.ts`).
 */
export function OwnerFilter({
  assigned,
  myUserId,
  onChange,
}: {
  assigned: string | null;
  myUserId: string | null;
  onChange: (assigned: string | null) => void;
}): React.JSX.Element {
  const label =
    assigned === null
      ? 'Owner'
      : assigned === UNASSIGNED
        ? 'Unassigned'
        : assigned === myUserId
          ? 'Mine'
          : `Owner #${assigned}`;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={cn(CHIP, assigned === null ? '' : 'text-fg')}>
        {label}
        <ChevronDown aria-hidden="true" className="size-3.5" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 w-[200px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          <MenuItem onSelect={() => onChange(null)} selected={assigned === null}>
            Anyone
          </MenuItem>
          {myUserId === null ? null : (
            <MenuItem onSelect={() => onChange(myUserId)} selected={assigned === myUserId}>
              Assigned to me
            </MenuItem>
          )}
          <MenuItem onSelect={() => onChange(UNASSIGNED)} selected={assigned === UNASSIGNED}>
            Unassigned
          </MenuItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({
  children,
  onSelect,
  selected = false,
  disabled = false,
  tone = 'default',
}: {
  children: React.ReactNode;
  onSelect: () => void;
  selected?: boolean;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}): React.JSX.Element {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-body13 outline-none',
        tone === 'danger' ? 'text-danger-fg' : 'text-fg',
        'data-[highlighted]:bg-subtle data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-muted',
      )}
    >
      {children}
      {selected ? <span aria-hidden="true" className="text-indigo">•</span> : null}
    </DropdownMenu.Item>
  );
}

/* ──────────────────────────────────────────────────────────── search field ── */

/**
 * `?q=` — debounced, so typing does not fire a request per keystroke against a `LIKE`
 * scan (`inquiry-filters.ts` records that the search is a scan within one country).
 *
 * The URL is updated with `replace`, so a six-character search leaves ONE history entry
 * instead of six the Back button has to walk through.
 */
export function SearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (q: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const [mirrored, setMirrored] = useState(value);

  /**
   * Keep the box in step when the URL changes from ELSEWHERE — the Back button, or the empty
   * state's "Clear filters".
   *
   * This is React's documented "adjust state when a prop changes" pattern rather than an
   * effect (react.dev, "You Might Not Need an Effect"), which is also what
   * `react-hooks/set-state-in-effect` asks for. It cannot eat keystrokes: the debounce below
   * resets on every keystroke, so when `onChange` finally lands, `value` already equals
   * `draft` and `setDraft(value)` is a no-op. Only a genuinely external change differs.
   */
  if (value !== mirrored) {
    setMirrored(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onChange(draft), 250);
    return () => window.clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className="flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5">
      <Search aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Search subject or customer"
        aria-label="Search inquiries by subject or customer"
        className="w-[200px] min-w-0 bg-transparent text-body13 text-fg outline-none placeholder:text-fg-muted"
      />
      {draft === '' ? null : (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
          aria-label="Clear search"
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── view toggle ── */

export function ViewToggle({
  view,
  onChange,
}: {
  view: InquiryView;
  onChange: (view: InquiryView) => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label="View"
      className="flex items-center overflow-hidden rounded-sm border border-line bg-surface"
    >
      <ToggleCell
        active={view === 'table'}
        label="Table view"
        onSelect={() => onChange('table')}
      >
        <Rows3 aria-hidden="true" className="size-4" />
      </ToggleCell>
      <ToggleCell
        active={view === 'board'}
        label="Board view"
        onSelect={() => onChange('board')}
      >
        <Columns3 aria-hidden="true" className="size-4" />
      </ToggleCell>
    </div>
  );
}

function ToggleCell({
  active,
  label,
  onSelect,
  children,
}: {
  active: boolean;
  label: string;
  onSelect: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      title={label}
      onClick={onSelect}
      className={cn(
        'flex size-8 items-center justify-center transition-colors',
        active ? 'bg-indigo-soft text-indigo' : 'text-fg-muted hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────── the strip ── */

export function InquiryToolbar({
  state,
  counts,
  total,
  myUserId,
  onPatch,
}: {
  state: InquiryQueryState;
  counts: Readonly<Record<InquiryStage, number>> | null;
  total: number | null;
  myUserId: string | null;
  onPatch: (patch: Partial<InquiryQueryState>, options?: { replace?: boolean }) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <StageTabs
        stage={state.stage}
        counts={counts}
        total={total}
        onSelect={(stage) => onPatch({ stage })}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={state.q} onChange={(q) => onPatch({ q }, { replace: true })} />
        <OwnerFilter
          assigned={state.assigned}
          myUserId={myUserId}
          onChange={(assigned) => onPatch({ assigned })}
        />
        <ViewToggle view={state.view} onChange={(view) => onPatch({ view })} />
      </div>
    </div>
  );
}
